import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Stack,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { IntegrationEndpoint } from "@modules/erpIntegrations/types";
import { EndpointFormValues, endpointSchema } from "@modules/erpIntegrations/validations";

import { EndpointFieldList } from "./EndpointFieldList";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
const ENCODINGS = ["", "json", "form", "multipart", "query", "none"] as const;

interface EndpointDialogProps {
  integrations: Array<{ id: number; name: string }>;
  defaultIntegrationId?: number;
  endpoint: IntegrationEndpoint | "new" | null;
  isSaving: boolean;
  onClose: () => void;
  onOpenHistory: (endpoint: IntegrationEndpoint) => void;
  onSubmit: (data: EndpointFormValues, existing: IntegrationEndpoint | null) => void;
}

function toFormFields(items: IntegrationEndpoint["query"] | undefined) {
  return (items ?? []).map((item) => ({
    key: item.key ?? "",
    label: item.label ?? "",
    required: Boolean(item.required),
    default: item.default ?? "",
    prefix: item.prefix ?? "",
    from_access_token: Boolean(item.from_access_token),
  }));
}

function toPayload(data: EndpointFormValues): EndpointFormValues {
  const clean = (items: EndpointFormValues["query"]) =>
    items.map((item) => {
      const field: EndpointFormValues["query"][number] = {
        key: item.key.trim(),
        required: Boolean(item.required),
      };
      const label = item.label?.trim();
      const fallback = item.default?.trim();
      const prefix = item.prefix?.trim();
      if (label) field.label = label;
      if (fallback) field.default = fallback;
      if (prefix) field.prefix = prefix;
      field.from_access_token = Boolean(item.from_access_token);
      return field;
    });

  return {
    ...data,
    slug: data.slug.trim(),
    label: data.label.trim(),
    description: data.description?.trim() ?? "",
    path: data.path.trim(),
    query: clean(data.query),
    headers: clean(data.headers),
    body: clean(data.body),
  };
}

export function EndpointDialog({
  integrations,
  defaultIntegrationId,
  endpoint,
  isSaving,
  onClose,
  onOpenHistory,
  onSubmit,
}: EndpointDialogProps) {
  const translate = useTranslate();
  const isOpen = endpoint !== null;
  const existing = endpoint && endpoint !== "new" ? endpoint : null;

  const buildDefaults = (): EndpointFormValues => ({
    integration: existing?.integration ?? defaultIntegrationId ?? ("" as unknown as number),
    slug: existing?.slug ?? "",
    label: existing?.label ?? "",
    description: existing?.description ?? "",
    method: existing?.method ?? "GET",
    path: existing?.path ?? "",
    query: toFormFields(existing?.query),
    headers: toFormFields(existing?.headers),
    body: toFormFields(existing?.body),
    body_encoding: existing?.body_encoding ?? "",
    active: existing?.active ?? true,
  });

  const methods = useForm<EndpointFormValues>({
    resolver: zodResolver(endpointSchema()),
    defaultValues: buildDefaults(),
  });

  useEffect(() => {
    if (!isOpen) return;
    methods.reset(buildDefaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?.id, defaultIntegrationId]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <FormProvider {...methods}>
        <form
          onSubmit={(event) => {
            event.stopPropagation();
            methods.handleSubmit((data) => onSubmit(toPayload(data), existing))(event);
          }}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <span>
                {existing
                  ? translate("erp_integrations.endpoints.edit_title")
                  : translate("erp_integrations.endpoints.create_title")}
              </span>
              {existing && (
                <Button type="button" size="small" onClick={() => onOpenHistory(existing)}>
                  {translate("erp_integrations.actions.view_history")}
                </Button>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12 }}>
                <Form.Select
                  name="integration"
                  label={translate("erp_integrations.endpoints.integration")}
                  options={integrations.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  disabled={Boolean(existing)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Form.Input
                  name="label"
                  label={translate("erp_integrations.endpoints.label")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Form.Input
                  name="slug"
                  label={translate("erp_integrations.endpoints.slug")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Input
                  name="description"
                  label={translate("erp_integrations.endpoints.description")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Form.Select
                  name="method"
                  label={translate("erp_integrations.endpoints.method")}
                  options={METHODS.map((method) => ({ value: method, label: method }))}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Form.Input
                  name="path"
                  label={translate("erp_integrations.endpoints.path")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Form.Select
                  name="body_encoding"
                  label={translate("erp_integrations.endpoints.body_encoding")}
                  options={ENCODINGS.map((encoding) => ({
                    value: encoding,
                    label: translate(
                      encoding
                        ? `erp_integrations.endpoints.encoding.${encoding}`
                        : "erp_integrations.endpoints.encoding.auto"
                    ),
                  }))}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <EndpointFieldList
                  name="headers"
                  title={translate("erp_integrations.endpoints.headers")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <EndpointFieldList
                  name="query"
                  title={translate("erp_integrations.endpoints.query")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <EndpointFieldList
                  name="body"
                  title={translate("erp_integrations.endpoints.body")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Switch
                  name="active"
                  label={translate("erp_integrations.endpoints.active")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {translate("buttons.cancel")}
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving}>
              {translate("buttons.save")}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
